import { Close, Monitor, CameraIndoor, PhotoCameraFront } from '@mui/icons-material';
import { useState, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Rnd } from 'react-rnd';
import { makeStyles } from 'tss-react/mui';
import { ENTITIES, LS_PIP_STATE, STREAM_TYPES } from '../consts';
import { messages } from '../intl';
import { setIsPip } from '../store/ui/actions';
import { getTypeFilter } from '../store/ui/selectors';
import useInterval from '../utils/useInterval';
import VideoBox from './ExamPreview/VideoBox';
import { syncVideos } from './ExamPreview/VideoControlBar';

const useStyles = makeStyles()((theme) => ({
	bounding: {
		zIndex: 1250,
		position: 'fixed',
		'& .react-draggable': { overflow: 'hidden', boxShadow: '0 0 20px #666', height: 'fit-content !important' },
		'& .dot': { top: '23px' },
		'& .resize': { left: '9px', position: 'absolute', pointerEvents: 'none', zIndex: 120, top: ' 8px' },
		'& .source': {
			position: 'absolute',
			top: '8px',
			zIndex: 10,
			display: 'flex',
			gap: '10px',
			left: '12px',
			color: 'white',
			'& div': { cursor: 'pointer' },
			'& svg': { color: 'white' },
		},
		'& .title': {
			color: 'white',
			position: 'absolute',
			top: 0,
			background: theme.palette.primary.main,
			width: 'calc(100% - 20px)',
			left: '0',
			padding: '8px 10px 5px 10px',
			display: 'flex',
			zIndex: 1,
			pointerEvents: 'none',
			justifyContent: 'center',
		},
		'& .close': {
			position: 'absolute',
			top: '8px',
			zIndex: 10,
			display: 'flex',
			cursor: 'pointer',
			gap: '10px',
			right: '12px',
			'& svg': { color: 'white' },
		},
		'& #videoContainer': {
			width: '100% !important',
			paddingTop: '32px',
			background: 'black',
			overflow: 'hidden',
			'& video': { width: '100% !important', height: 'auto !important' },
		},
		'& .control-bar': {
			background: 'white',
			padding: '10px',
		},
		'& .MuiPopover-root': {
			position: 'absolute !important',
			'& .MuiPaper-root': {
				position: 'absolute !important',
				maxHeight: '230px !important',
				top: '-130px !important',
				left: '0 !important',
			},
		},
	},
}));

const PIP_MIN_WIDTH = 460;
const PIP_TITLE_BAR_HEIGHT = 32;
const PIP_CONTROL_BAR_HEIGHT = 162;
const PIP_BARS_HEIGHT = PIP_TITLE_BAR_HEIGHT + PIP_CONTROL_BAR_HEIGHT;
const PIP_MIN_HEIGHT = PIP_BARS_HEIGHT + 50;
const PIP_MARGIN = 10;

const loadSavedState = () => {
	try {
		const raw = localStorage.getItem(LS_PIP_STATE);
		if (!raw) return null;
		const { x, y, width, height } = JSON.parse(raw);
		if (![x, y, width, height].every((v) => typeof v === 'number')) return null;
		if (width < PIP_MIN_WIDTH || height < PIP_MIN_HEIGHT) return null;
		return { x, y, width, height };
	} catch {
		return null;
	}
};

const fitToViewport = ({ x, y, width, height }) => {
	let w = Math.min(width, window.innerWidth - 2 * PIP_MARGIN);
	let h = Math.round(((height - PIP_BARS_HEIGHT) * w) / width) + PIP_BARS_HEIGHT;

	const maxHeight = window.innerHeight - 2 * PIP_MARGIN;
	if (h > maxHeight) {
		w = Math.round((w * (maxHeight - PIP_BARS_HEIGHT)) / (h - PIP_BARS_HEIGHT));
		h = maxHeight;
	}

	w = Math.max(w, PIP_MIN_WIDTH);
	h = Math.max(h, PIP_MIN_HEIGHT);

	return {
		x: Math.max(0, Math.min(x, window.innerWidth - w - PIP_MARGIN)),
		y: Math.max(0, Math.min(y, window.innerHeight - h - PIP_MARGIN)),
		width: w,
		height: h,
	};
};

const saveState = (x, y, node) => {
	if (!node) return;
	localStorage.setItem(LS_PIP_STATE, JSON.stringify({ x, y, width: node.offsetWidth, height: node.offsetHeight }));
};

const getMainVideoPipDimensions = () => {
	const video = document.querySelector('video.examVideo');
	if (!video || !video.videoWidth || !video.videoHeight) return { width: PIP_MIN_WIDTH, height: PIP_MIN_HEIGHT };

	const videoRatio = video.videoWidth / video.videoHeight;
	const elementRatio = video.clientWidth / video.clientHeight;

	const contentWidth = videoRatio > elementRatio ? video.clientWidth : Math.round(video.clientHeight * videoRatio);
	const width = Math.max(contentWidth, PIP_MIN_WIDTH);
	const height = PIP_TITLE_BAR_HEIGHT + Math.round(width / videoRatio) + PIP_CONTROL_BAR_HEIGHT;

	return { width, height };
};

const Pip = () => {
	const { classes } = useStyles();
	const intl = useIntl();
	const typeFilter = useSelector(getTypeFilter);
	const [pipTypeFilter, setPipTypeFilter] = useState(typeFilter);
	const dispatch = useDispatch();

	const fallback = useMemo(() => getMainVideoPipDimensions(), []);
	const saved = useMemo(() => loadSavedState(), []);

	const rndDefault = fitToViewport(
		saved ?? {
			...fallback,
			x: window.innerWidth - fallback.width - PIP_MARGIN,
			y: window.innerHeight - fallback.height - PIP_MARGIN,
		}
	);

	// Handle current time difference
	useInterval(() => {
		const htmlVideos = document.querySelectorAll('video.examVideo');
		const v1 = htmlVideos?.[0]?.currentTime ?? 0;
		const v2 = htmlVideos?.[1]?.currentTime ?? 0;

		if (v1.toFixed(2) !== v2.toFixed(2)) syncVideos('currentTime', v1);
	}, 2000);

	return (
		<div className={classes.bounding} style={{ top: 0, left: 0 }}>
			<Rnd
				default={rndDefault}
				minWidth={PIP_MIN_WIDTH}
				minHeight={PIP_MIN_HEIGHT}
				onDragStop={(e, d) => saveState(d.x, d.y, d.node)}
				onResizeStop={(e, dir, ref, delta, pos) => saveState(pos.x, pos.y, ref)}
			>
				<div className="source">
					<div title={intl.formatMessage(messages.room)} onClick={() => setPipTypeFilter(ENTITIES.VIDEO_ROOM)}>
						<CameraIndoor fontSize="10" />
					</div>
					<div title={intl.formatMessage(messages.screen)} onClick={() => setPipTypeFilter(STREAM_TYPES.SCREEN)}>
						<Monitor fontSize="10" />
					</div>
					<div title={intl.formatMessage(messages.camera)} onClick={() => setPipTypeFilter(STREAM_TYPES.WEBCAM)}>
						<PhotoCameraFront fontSize="10" />
					</div>
				</div>
				<div className="title">{intl.formatMessage(messages[pipTypeFilter])}</div>
				<div className="close" title={intl.formatMessage(messages.close)} onClick={() => dispatch(setIsPip(false))}>
					<Close fontSize="10" />
				</div>
				<VideoBox pipTypeFilter={pipTypeFilter} />
			</Rnd>
		</div>
	);
};

export default Pip;
